/**
 * Pure Saleor/CMS merge for PlaceholderModelStory → ProductStory props.
 * Kept outside CmsRenderer so Sonar coverage can target the merge rules without
 * rendering the full CMS dispatch table.
 */

export type ModelStoryFill = {
  title?: string;
  content?: string;
  image?: { url: string; alt?: string };
};

export type ModelStoryCmsFields = {
  storyTitle?: string | null;
  storyContent?: string | null;
  media?: { url?: string | null; alt?: string | null } | null;
  mediaAlignment?: string | null;
  backgroundColor?: string | null;
};

export type ResolvedModelStory = {
  title?: string;
  /** Empty string still drives an always-rendered body shell in ProductStory. */
  content: string;
  image?: { url: string; alt: string };
  mediaReversed: boolean;
  backgroundColor?: string;
  /** Straps stay centered; watches sit right-bottom in the media panel. */
  imageAlign: 'center' | 'right';
};

/** Prefer CMS media URL, else Saleor image; always apply a non-empty alt. */
export function resolveStoryImage(
  cmsMedia: ModelStoryCmsFields['media'] | undefined,
  saleorImage: ModelStoryFill['image'] | undefined,
  fallbackAlt: string
): { url: string; alt: string } | undefined {
  if (cmsMedia?.url) {
    return {
      url: cmsMedia.url,
      alt: cmsMedia.alt || fallbackAlt,
    };
  }

  if (saleorImage?.url) {
    return {
      url: saleorImage.url,
      alt: saleorImage.alt || fallbackAlt,
    };
  }

  return undefined;
}

/**
 * Merge CMS PlaceholderModelStory fields with Saleor `modelStory` fill.
 *
 * Priority:
 * - title / content — Saleor first, then CMS
 * - image — CMS media first, then Saleor
 * - mediaAlignment / background — CMS only ("left" → mediaReversed)
 * - imageAlign — straps center; watches (and anything else) right
 */
export function resolveModelStoryViewModel({
  cms,
  saleor,
  productName,
  productSku,
  productType,
}: {
  cms: ModelStoryCmsFields;
  saleor?: ModelStoryFill | null;
  productName?: string;
  productSku?: string;
  productType?: string;
}): ResolvedModelStory {
  const rawTitle = saleor?.title || cms.storyTitle || undefined;
  const title = rawTitle ? rawTitle.replace(/\[\[(\w+)\]\]/g, '$1') : undefined;

  const cmsContent = typeof cms.storyContent === 'string' ? cms.storyContent : '';
  const content = saleor?.content || cmsContent || '';

  const fallbackAlt =
    productName && productSku
      ? `${productName} - ${productSku}`
      : productName || title || 'Model story';

  const mediaAlignment = cms.mediaAlignment?.toLowerCase() ?? '';

  return {
    title,
    content,
    image: resolveStoryImage(cms.media, saleor?.image, fallbackAlt),
    mediaReversed: mediaAlignment.includes('left'),
    backgroundColor: cms.backgroundColor || undefined,
    imageAlign: productType === 'strap' ? 'center' : 'right',
  };
}
